import { useCallback, useEffect, useState } from "react";

import { askConfig, saveAskLimits, type AskLimits } from "@/lib/ask";
import {
  chooseModel,
  contextLabel,
  groupByVendor,
  listModels,
  matches,
  price,
  refreshModels,
  type ModelCatalog,
} from "@/lib/models";

import { Panel, PanelButton } from "./Setup";

/**
 * Models — which model translation and Ask each use (日本-325).
 *
 * **Two surfaces, chosen separately, from a catalogue the server fetches live.** The model used to
 * be `NIHONGONATOR_TRANSLATE_MODEL`, which was the right shape while it was an engineer's decision
 * made once at deploy time and the wrong one for comparing two candidates on real sentences.
 *
 * The panel's job beyond the two dropdowns is to make three invisible things visible:
 *
 * - **Ask only lists models that can call tools.** The server filters, not this component — but the
 *   note says so, because a shorter list is otherwise just a shorter list.
 * - **A slug the catalogue does not know is a warning, not an error.** The commonest cause is a
 *   catalogue that could not be fetched, not a model that does not exist.
 * - **Price and context, per model, at the point of choosing.** Ask re-sends a growing transcript
 *   every turn, so a tenfold difference in input price is the difference between cents and dollars
 *   a session — and nothing else in the app would ever tell you.
 */

const SURFACES: { surface: string; title: string; note: string }[] = [
  {
    surface: "translate",
    title: "翻訳",
    note: "1文につき1回。安価なモデルでも十分です。",
  },
  {
    surface: "ask",
    title: "質問",
    note: "ツールを呼ぶ会話。ツールを呼べるモデルだけを載せています。",
  },
];

export function ModelsPanel({
  onError,
  onSuccess,
}: {
  onError: (error: unknown) => void;
  onSuccess: () => void;
}) {
  const [catalogs, setCatalogs] = useState<Record<string, ModelCatalog>>({});

  const load = useCallback(() => {
    for (const { surface } of SURFACES) {
      void listModels(surface)
        .then((catalog) => setCatalogs((current) => ({ ...current, [surface]: catalog })))
        .catch(onError);
    }
  }, [onError]);

  useEffect(load, [load]);

  const refresh = async () => {
    try {
      for (const { surface } of SURFACES) {
        const catalog = await refreshModels(surface);
        setCatalogs((current) => ({ ...current, [surface]: catalog }));
      }
      onSuccess();
    } catch (e) {
      onError(e);
    }
  };

  const choose = async (surface: string, selected: string) => {
    try {
      const selection = await chooseModel(surface, selected);
      setCatalogs((current) => {
        const catalog = current[surface];
        return catalog ? { ...current, [surface]: { ...catalog, selection } } : current;
      });
      onSuccess();
    } catch (e) {
      onError(e);
    }
  };

  // Either surface answers this — it is a property of the deployment, not of a model.
  const configured = SURFACES.some(({ surface }) => catalogs[surface]?.configured);
  const loaded = SURFACES.some(({ surface }) => catalogs[surface]);

  return (
    <Panel
      title="モデル"
      subtitle="翻訳と質問 · OpenRouter経由"
      actions={<PanelButton onClick={() => void refresh()}>一覧を更新</PanelButton>}
    >
      {loaded && !configured && (
        <p className="border-b border-line-soft px-[18px] py-[10px] text-secondary text-[color:var(--ink-3)]">
          この環境に <code className="font-mono">OPENROUTER_API_KEY</code> がありません。
          {"モデルは選べますが、鍵が入るまで翻訳と質問は表示されません。"}
        </p>
      )}
      {SURFACES.map(({ surface, title, note }) => (
        <SurfaceRow
          key={surface}
          title={title}
          note={note}
          catalog={catalogs[surface]}
          onChoose={(selected) => void choose(surface, selected)}
        />
      ))}
      <AskBudgetRow onError={onError} onSuccess={onSuccess} />
    </Panel>
  );
}

function SurfaceRow({
  title,
  note,
  catalog,
  onChoose,
}: {
  title: string;
  note: string;
  catalog: ModelCatalog | undefined;
  onChoose: (selected: string) => void;
}) {
  const [filter, setFilter] = useState("");
  const [slug, setSlug] = useState("");

  if (!catalog) {
    return (
      <div className="border-b border-line-soft px-[18px] py-3 last:border-b-0">
        <span className="text-secondary text-ink-3">{title}を読み込み中…</span>
      </div>
    );
  }

  const { selection, models } = catalog;
  const visible = models.filter((model) => matches(model, filter));
  // The chosen model must stay selectable even when the filter hides it, or typing in the box
  // silently changes what the dropdown claims is in use.
  const inList = visible.some((model) => model.id === selection.selected);
  const card = selection.card;

  return (
    <div className="flex flex-col gap-[10px] border-b border-line-soft px-[18px] py-3 last:border-b-0">
      <div className="flex flex-wrap items-baseline gap-[10px]">
        <span className="font-jp text-eyebrow font-semibold uppercase tracking-[.1em] text-ink-3">
          {title}
        </span>
        <span className="text-meta text-ink-4">{note}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="絞り込み"
          aria-label={`${title}のモデルを絞り込む`}
          className="w-[120px] rounded-[var(--r-2)] border border-line-strong bg-void px-2 py-[6px] text-[14px]/[1.2] text-ink-2"
        />
        <select
          value={selection.selected}
          onChange={(e) => onChoose(e.target.value)}
          aria-label={`${title}のモデル`}
          className="min-w-[260px] flex-1 rounded-[var(--r-2)] border border-line-strong bg-void px-2 py-[6px] text-[14px]/[1.2] text-ink"
        >
          {!inList && <option value={selection.selected}>{selection.selected}</option>}
          {groupByVendor(visible).map(([vendor, entries]) => (
            <optgroup key={vendor} label={vendor}>
              {entries.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name} · {price(model.promptPerM)}/{price(model.completionPerM)} ·{" "}
                  {contextLabel(model.contextLength)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {!selection.isDefault && (
          <PanelButton onClick={() => onChoose("")}>既定を使う</PanelButton>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-[14px] gap-y-1 text-meta text-ink-4">
        <span className="font-mono text-ink-3">{selection.selected}</span>
        {selection.isDefault && <span>この環境の既定</span>}
        {card && (
          <>
            <span>
              入力 {price(card.promptPerM)} · 出力 {price(card.completionPerM)} / 100万
            </span>
            <span>文脈 {contextLabel(card.contextLength)}</span>
            {/* Not decoration: Ask re-sends a growing transcript every turn, and a model with no
                cache price makes that cost full price each time. */}
            <span>{card.caches ? `キャッシュ可 · 読み ${price(card.cacheReadPerM)}` : "キャッシュ不可"}</span>
          </>
        )}
      </div>

      {card?.floating && (
        <p className="text-meta text-ink-4">
          浮動エイリアスです。指す先はベンダーが新版を出すたびに変わります。
        </p>
      )}
      {card?.expiresAt && (
        <p className="text-meta text-[color:var(--ink-3)]">
          一覧ではこのモデルは {card.expiresAt} に提供終了とされています。
        </p>
      )}
      {!card && (
        <p className="text-meta text-[color:var(--ink-3)]">
          {"このモデルは一覧にありません。多くの場合は一覧を取得できなかっただけで、モデルが間違っているわけではありません。そのまま使われます。"}
        </p>
      )}
      {catalog.error && <p className="text-meta text-[color:var(--ink-3)]">{catalog.error}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="または OpenRouter のスラッグを貼り付け"
          aria-label={`${title}のモデルスラッグ`}
          className="w-[240px] rounded-[var(--r-2)] border border-line-strong bg-void px-2 py-[6px] text-[14px]/[1.2] text-ink-2"
        />
        <PanelButton
          disabled={!slug.trim()}
          onClick={() => {
            onChoose(slug.trim());
            setSlug("");
          }}
        >
          このスラッグを使う
        </PanelButton>
      </div>
    </div>
  );
}


/**
 * What a conversation may cost, and how much of it keeps its tool results.
 *
 * **The budget covers the transcript only** — the system prompt, the tool schemas and the folder
 * envelope are priced separately and deliberately excluded, because they are the stable half that
 * the prompt cache holds and counting them here would let a long envelope quietly eat the
 * conversation it exists to support.
 *
 * `Tool memory` is the interesting number. Full tool results are kept for that many recent turns
 * and then collapse to their one-line summary, which the agent can un-collapse on demand. Raising
 * it buys the model more detail about older calls and costs tokens on every turn.
 */
function AskBudgetRow({
  onError,
  onSuccess,
}: {
  onError: (error: unknown) => void;
  onSuccess: () => void;
}) {
  const [limits, setLimits] = useState<AskLimits | null>(null);

  useEffect(() => {
    void askConfig()
      .then((config) => setLimits(config.limits))
      .catch(() => setLimits(null));
  }, []);

  if (!limits) return null;

  const save = async (patch: Partial<AskLimits>) => {
    try {
      const next = await saveAskLimits(patch);
      setLimits(next.limits);
      onSuccess();
    } catch (e) {
      onError(e);
    }
  };

  const fields: { key: keyof AskLimits; label: string; hint: string }[] = [
    { key: "historyBudgetTokens", label: "履歴の上限", hint: "送信する履歴のトークン数" },
    { key: "toolMemoryTurns", label: "ツールの記憶", hint: "完全な結果を保つターン数" },
    { key: "maxRecallsPerTurn", label: "再呼び出し", hint: "1返信あたり" },
  ];

  return (
    <div className="flex flex-col gap-[10px] border-b border-line-soft px-[18px] py-3 last:border-b-0">
      <div className="flex flex-wrap items-baseline gap-[10px]">
        <span className="font-jp text-eyebrow font-semibold uppercase tracking-[.1em] text-ink-3">
          質問の上限
        </span>
        <span className="text-meta text-ink-4">
          会話1回あたりの費用。プロンプトと文脈エンベロープは別勘定です。
        </span>
      </div>
      <div className="flex flex-wrap items-end gap-[14px]">
        {fields.map(({ key, label, hint }) => (
          <label key={key} className="flex flex-col gap-[3px]">
            <span className="text-meta text-ink-3">{label}</span>
            <input
              type="number"
              defaultValue={limits[key]}
              onBlur={(e) => {
                // A blank box is `Number("") === 0`, which is a real number and a real 400 from
                // the server; a typed decimal is a 422 at the body edge. Neither is a value the
                // reader chose, so the field is put back rather than sent — `defaultValue` cannot
                // resync it after the fact, because React ignores later changes to one.
                const raw = e.target.value.trim();
                const value = Number(raw);
                if (raw === "" || !Number.isInteger(value)) {
                  e.target.value = String(limits[key]);
                  return;
                }
                if (value !== limits[key]) void save({ [key]: value });
              }}
              className="w-[110px] rounded-[var(--r-2)] border border-line-strong bg-void px-2 py-[6px] text-[14px]/[1.2] text-ink"
            />
            <span className="text-meta text-ink-4">{hint}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
